// @flow
import Head from 'next/head';
import React, { Fragment } from 'react';
import { constants } from '@clubmed/utils';

import ReactAppContext from 'Context/ReactAppContext';
import SectionsBlocksContainer from 'Components/widgets/sections-blocks-container/SectionsBlocksContainer.jsx';
import { Hero } from 'Components/widgets/hero/Hero.jsx';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { FacetSearch } from 'Components/widgets/facet-search/FacetSearch.jsx';
import { STATIC_PAGE_TYPES } from 'Tracking/constants';
import { TrackingPageLoad } from 'Tracking/tracking-pageload/TrackingPageLoad.jsx';
import SectionBlocks from 'Components/widgets/section-blocks/SectionBlocks.jsx';
import { HOME } from 'Routes/constants';
import { formatContacts } from 'Views/home/HomeView.helper';

type Props = {
  config: {
    ...$Exact<CMSContent>,
    footer: CMSFooter,
  },
};

export const HomeLegacyView = ({ config }: Props) => {
  const { locale, originalUrl: origin } = React.useContext(ReactAppContext);
  const {
    home: { sections, immersive, pushedBlocks, seo },
    footer: {
      seo: { description },
    },
    discoverClubMed,
    seo: commonSeo,
    zone,
  } = config;
  const contactPoints = commonSeo ? formatContacts(commonSeo.contacts, locale) : [];
  const metas = seo.meta.map(meta => <meta {...meta} key={JSON.stringify(meta)} />);
  const hrefLangs = seo.hrefLang.map(lang => (
    <link {...lang} rel="alternate" key={JSON.stringify(lang)} />
  ));
  const scripts = [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        url: origin,
        name: constants.WEBSITE_NAME,
        description,
        ...(contactPoints.length === 0 ? null : { contactPoint: contactPoints }),
      }),
    },
  ].map((script, idx) => (
    <script key={idx} type={script.type} dangerouslySetInnerHTML={{ __html: script.innerHTML }} />
  ));

  return (
    <Fragment>
      <Head>
        <title key="title">{seo.title}</title>
        {metas}
        {hrefLangs}
        {scripts}
      </Head>
      <TrackingPageLoad pageName="Shopping_Home" />
      <div className="HomeView" data-testid="legacy-home-view">
        <Hero hasOverlap {...immersive} url={immersive.backgroundURL} />
        <section className="Line Line-Search">
          <SiteContainer>
            <FacetSearch
              i18n={config}
              searchPageRouteName="search"
              shouldRedirectToResortPage
              withSearchForm
            />
          </SiteContainer>
        </section>
        {pushedBlocks.length > 0 && (
          <section className="Line Line-Push">
            <SiteContainer>
              <SectionBlocks allBlocks={pushedBlocks} />
            </SiteContainer>
          </section>
        )}
        <SectionsBlocksContainer
          discoverClubMed={discoverClubMed}
          destination={zone}
          sections={sections}
        />
      </div>
    </Fragment>
  );
};

HomeLegacyView.pageId = HOME;
HomeLegacyView.pageType = STATIC_PAGE_TYPES.SHOPPING_HOME;
